import {
	CloudIcon,
	MagnifyingGlassIcon,
	PlusIcon,
	AdjustmentsHorizontalIcon,
} from '@heroicons/react/24/outline';
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import moment from 'moment';

import { deleteDocument, getDocuments } from '../../api/documents';
import { removeAssetDocument } from '../../api';
import { getModuleCategories } from '../../api/modules';

import Button from '../../components/Button';
import { MainContent, PageHeading } from '../../components/Shell/Content';
import { DataTable, FilterDropdown } from '../../components/nextgen';
import EllipsisDropdown, {
	EllipsisDropdownItem,
} from '../../components/EllipsisDropdown';

import useAsync from '../../hooks/useAsync';
import useModal from '../../hooks/useModal';
import usePrevious from '../../hooks/usePrevious';

const DOCUMENTS_MODULE_ID = process.env.REACT_APP_MODULE_ID_DOCUMENTS;

const Documents = ({ item, bare }) => {
	const [searchParams] = useSearchParams();
	const assetIdFromUrl = searchParams.get('assetId');

	const { setOpen, setClose } = useModal('deleteModal');
	const { setOpen: openUploadDocumentsModal } = useModal('uploadDocuments');
	const {
		open: isDocumentPreviewOpen,
		setOpen: openDocumentPreview,
		setClose: closeDocumentPreview,
	} = useModal('documentPreview');

	const { open: isUploadDocumentsOpen } = useModal('uploadDocuments');

	// Filters
	const [search, setSearch] = useState('');
	const [categoryFilter, setCategoryFilter] = useState('all');

	// Track currently previewed document ID for refresh
	const previewedDocumentIdRef = useRef(null);
	const shouldRefreshPreviewRef = useRef(false);

	const { execute: onRemoveDocument } = useAsync(removeAssetDocument, {
		immediate: false,
	});

	const { execute: onDeleteDocument } = useAsync(deleteDocument, {
		immediate: false,
	});

	const {
		value: documents,
		execute: onRefreshDocuments,
		status: loadingStatus,
	} = useAsync(getDocuments, {
		defaultPayload: {
			pageSize: null,
			...(item?.id && { assetId: item?.id }),
			...(assetIdFromUrl && !item?.id && { assetId: assetIdFromUrl }),
		},
	});

	const { value: categories } = useAsync(getModuleCategories, {
		defaultPayload: { id: DOCUMENTS_MODULE_ID },
		silent: true,
	});

	const prevIsUploadDocumentsOpen = usePrevious(isUploadDocumentsOpen);

	useEffect(() => {
		if (!isUploadDocumentsOpen && prevIsUploadDocumentsOpen) {
			// Mark that we need to refresh the preview after documents reload
			if (previewedDocumentIdRef.current) {
				shouldRefreshPreviewRef.current = true;
			}
			onRefreshDocuments(null, null, {
				silent: true,
			});
		}
	}, [isUploadDocumentsOpen]);

	// Update the preview modal with fresh document data after refresh
	useEffect(() => {
		if (
			shouldRefreshPreviewRef.current &&
			previewedDocumentIdRef.current &&
			isDocumentPreviewOpen &&
			documents?.rows
		) {
			const updatedDoc = documents.rows.find(
				(d) => d.id === previewedDocumentIdRef.current,
			);
			if (updatedDoc) {
				shouldRefreshPreviewRef.current = false;
				openDocumentPreview({
					document: updatedDoc,
					enableEdit: true,
					enableDelete: true,
					onEdit: (document) => {
						openUploadDocumentsModal({
							id: document?.id,
							moduleId: DOCUMENTS_MODULE_ID,
						});
					},
					onDelete: (document) => {
						setOpen({
							title: 'Are you sure you want to permanently delete this document?',
							description: 'This action cannot be undone.',
							confirmText: 'Delete Document',
							onConfirm: () =>
								onDeleteDocument(
									{
										id: document?.id,
									},
									() => {
										setClose();
										closeDocumentPreview();
										previewedDocumentIdRef.current = null;
										onRefreshDocuments(null, null, {
											silent: true,
										});
									},
								),
						});
					},
				});
			}
		}
	}, [documents]);

	// Process documents (search and filter)
	const processedDocuments = useMemo(() => {
		let result = documents?.rows || [];

		// Asset filter from URL (hidden filter) - only if not already applied in fetch
		if (assetIdFromUrl && !item?.id) {
			result = result.filter((doc) => doc.assetId === assetIdFromUrl);
		}

		// Search
		if (search) {
			const q = search.toLowerCase();
			result = result.filter((doc) => doc.name?.toLowerCase().includes(q));
		}

		// Category filter
		if (categoryFilter !== 'all') {
			if (categoryFilter === 'uncategorized') {
				result = result.filter((doc) => !doc.categoryId);
			} else {
				result = result.filter((doc) => doc.categoryId === categoryFilter);
			}
		}

		return result;
	}, [documents, search, categoryFilter, assetIdFromUrl, item?.id]);

	const isLoading = loadingStatus === 'pending';

	// Handler to open document preview with edit and delete enabled
	const handleOpenDocumentPreview = (doc) => {
		previewedDocumentIdRef.current = doc?.id;
		openDocumentPreview({
			document: doc,
			enableEdit: true,
			enableDelete: true,
			onEdit: (document) => {
				openUploadDocumentsModal({
					id: document?.id,
					moduleId: DOCUMENTS_MODULE_ID,
				});
			},
			onDelete: (document) => {
				setOpen({
					title: 'Are you sure you want to permanently delete this document?',
					description: 'This action cannot be undone.',
					confirmText: 'Delete Document',
					onConfirm: () =>
						onDeleteDocument(
							{
								id: document?.id,
							},
							() => {
								setClose();
								closeDocumentPreview();
								onRefreshDocuments(null, null, {
									silent: true,
								});
							},
						),
				});
			},
		});
	};

	// Render component based on `bare` prop
	if (bare) {
		return (
			<div className="py-4">
				<div className="text-md my-3 font-medium tracking-tight text-gray-700 flex justify-between items-center border-b pb-3">
					Documents
					<Button
						type="secondary"
						size="sm"
						Icon={CloudIcon}
						onClick={() =>
							openUploadDocumentsModal({ moduleId: DOCUMENTS_MODULE_ID })
						}
					>
						Upload
					</Button>
				</div>

				<DataTable
					columns={[
						{
							header: 'Name',
							key: 'name',
							render: (doc) => doc?.name || 'Untitled',
						},
						{
							header: 'Category',
							key: 'category.name',
							render: (doc) => doc?.category?.name || 'Uncategorized',
						},
						{
							header: 'Uploaded',
							key: 'createdAt',
							render: (doc) =>
								doc?.createdAt
									? moment(doc?.createdAt).format('MMM D, YYYY')
									: '',
						},
					]}
					data={processedDocuments}
					loading={isLoading}
					onRowClick={(doc) => handleOpenDocumentPreview(doc)}
					rowActions={(doc) => (
						<EllipsisDropdown>
							<EllipsisDropdownItem
								onClick={() => {
									openUploadDocumentsModal({
										id: doc?.id,
										moduleId: DOCUMENTS_MODULE_ID,
									});
								}}
							>
								Edit
							</EllipsisDropdownItem>
							{item?.id ? (
								<EllipsisDropdownItem
									onClick={() =>
										setOpen({
											title: 'Are you sure you want to remove this document?',
											confirmText: 'Remove Document',
											onConfirm: () =>
												onRemoveDocument(
													{
														id: item?.id,
														documentId: doc?.id,
													},
													() => {
														setClose();
														onRefreshDocuments(null, null, {
															silent: true,
														});
													},
												),
										})
									}
								>
									Remove
								</EllipsisDropdownItem>
							) : (
								<EllipsisDropdownItem
									danger
									onClick={() =>
										setOpen({
											title:
												'Are you sure you want to permanently delete this document?',
											confirmText: 'Delete Document',
											onConfirm: () =>
												onDeleteDocument(
													{
														id: doc?.id,
													},
													() => {
														setClose();
														onRefreshDocuments(null, null, {
															silent: true,
														});
													},
												),
										})
									}
								>
									Delete
								</EllipsisDropdownItem>
							)}
						</EllipsisDropdown>
					)}
				/>
			</div>
		);
	}

	// Full page view
	return (
		<MainContent maxWidth="max-w-7xl">
			<PageHeading
				actions={[
					{
						name: 'Upload Document',
						onClick: () =>
							openUploadDocumentsModal({ moduleId: DOCUMENTS_MODULE_ID }),
						icon: PlusIcon,
					},
				]}
				divider={false}
			>
				Documents
			</PageHeading>

			{/* Search & Filters */}
			<div className="bg-white rounded-lg mb-6">
				<div className="flex flex-col lg:flex-row lg:items-center gap-4 py-4">
					{/* Search */}
					<div className="flex-1 relative border border-gray-200 rounded-xl">
						<MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
						<input
							type="text"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							placeholder="Search documents..."
							className="w-full pl-12 pr-4 py-2 text-sm bg-transparent border border-gray-200 lg:border-0 rounded-lg lg:rounded-none focus:outline-none focus:ring-0 placeholder:text-gray-400"
						/>
					</div>

					{/* Divider */}
					<div className="hidden lg:block w-px h-8 bg-gray-200" />

					{/* Filters */}
					<div className="flex items-center gap-3 flex-wrap">
						<AdjustmentsHorizontalIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
						{categories && categories.length > 0 && (
							<FilterDropdown
								label="Category"
								placeholder="All Categories"
								value={categoryFilter}
								options={[
									{ value: 'all', label: 'All Categories' },
									...categories.map((cat) => ({
										value: cat.id,
										label: cat.name,
									})),
									{ value: 'uncategorized', label: 'Uncategorized' },
								]}
								onChange={setCategoryFilter}
							/>
						)}
					</div>

					{/* Document Count */}
					<div className="text-sm text-gray-500">
						{processedDocuments.length} document
						{processedDocuments.length !== 1 ? 's' : ''}
					</div>
				</div>
			</div>

			{/* Documents Table */}
			<div className="hidden lg:block">
				<DataTable
					columns={[
						{
							header: 'Name',
							key: 'name',
							render: (doc) => (
								<div className="flex items-center gap-2">
									<span className="truncate">{doc?.name || 'Untitled'}</span>
								</div>
							),
						},
						{
							header: 'Category',
							key: 'category.name',
							render: (doc) => doc?.category?.name || 'Uncategorized',
						},
						{
							header: 'Uploaded',
							key: 'createdAt',
							render: (doc) =>
								doc?.createdAt
									? moment(doc?.createdAt).format('MMM D, YYYY')
									: '',
						},
					]}
					data={processedDocuments}
					keyField="id"
					loading={isLoading}
					onRowClick={(doc) => handleOpenDocumentPreview(doc)}
					containerClassName="border border-gray-200 rounded-lg"
					emptyTitle="No documents found"
					emptyDescription="Upload your first document to get started."
					compact
					pagination
					pageSize={25}
					pageSizeOptions={[10, 25, 50]}
				/>
			</div>

			{/* Mobile View */}
			<div className="lg:hidden space-y-3">
				{isLoading ? (
					<div className="text-center py-8 text-gray-500">Loading...</div>
				) : processedDocuments.length === 0 ? (
					<div className="text-center py-8">
						<CloudIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
						<p className="text-gray-900 font-medium">No documents found</p>
						<p className="text-gray-500 text-sm">
							Upload your first document to get started.
						</p>
					</div>
				) : (
					processedDocuments.map((doc) => (
						<div
							key={doc.id}
							className="border border-gray-200 rounded-lg p-4 cursor-pointer hover:border-gray-300 transition-colors bg-white"
							onClick={() => handleOpenDocumentPreview(doc)}
						>
							<div className="flex items-start gap-3">
								<CloudIcon className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
								<div className="flex-1 min-w-0">
									<h3 className="text-sm font-medium text-gray-900 mb-1 truncate">
										{doc?.name || 'Untitled'}
									</h3>
									<div className="text-xs text-gray-500 mb-2">
										{doc?.category?.name || 'Uncategorized'}
									</div>
									<div className="text-xs text-gray-400">
										{doc?.createdAt
											? moment(doc?.createdAt).format('MMM D, YYYY')
											: ''}
									</div>
								</div>
								<div onClick={(e) => e.stopPropagation()}>
									<EllipsisDropdown>
										<EllipsisDropdownItem
											onClick={() => {
												openUploadDocumentsModal({
													id: doc?.id,
													moduleId: DOCUMENTS_MODULE_ID,
												});
											}}
										>
											Edit
										</EllipsisDropdownItem>
										<EllipsisDropdownItem
											danger
											onClick={() =>
												setOpen({
													title:
														'Are you sure you want to permanently delete this document?',
													description: 'This action cannot be undone.',
													confirmText: 'Delete Document',
													onConfirm: () =>
														onDeleteDocument(
															{
																id: doc?.id,
															},
															() => {
																setClose();
																onRefreshDocuments(null, null, {
																	silent: true,
																});
															},
														),
												})
											}
										>
											Delete
										</EllipsisDropdownItem>
									</EllipsisDropdown>
								</div>
							</div>
						</div>
					))
				)}
			</div>
		</MainContent>
	);
};

export default Documents;
